// Intégration dans un Formulaire Shadcn

// Indique que ce composant doit être rendu côté client
"use client";

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Progress,
} from "@repo/ui";
import { useMutation } from "convex/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { api } from "../../../../../convex/_generated/api";
import {
  isBackendError,
  TemplateFormEnum,
  TypeEvalEnum,
  type FormTemplateProps,
  type PamiReponses,
} from "../../lib/types";
import { useFormStore } from "../../stores/formStore";
import {
  PAMI_QUESTIONS_PER_PAGE,
  pamiInstructions,
  pamiQuestions,
  type PamiStatement,
} from "./pami-data";
import { SortableItem } from "./sortable-item";

const schema = z.object(
  pamiQuestions.reduce(
    (acc, q) => {
      acc[q.name] = z.array(z.any()).length(3);
      return acc;
    },
    {} as Record<string, z.ZodType<any> | z.ZodTypeAny>,
  ),
);

type FormData = z.infer<typeof schema>;

export function ReorderForm({
  token,
  parcoursId,
  editable,
  evaluation,
  onComplete,
}: FormTemplateProps) {
  const {
    currentStep: currentPage,
    setCurrentStep: setCurrentPage,
    setStartTime,
    reset,
  } = useFormStore();
  const [questionsFinales, setQuestionsFinales] = useState<{
    langueMaternelle: boolean | null;
    bonnesConditions: boolean | null;
    reponsesSinceres: boolean | null;
  }>({
    langueMaternelle: null,
    bonnesConditions: null,
    reponsesSinceres: null,
  });

  const questionsPerPage = PAMI_QUESTIONS_PER_PAGE;
  const totalPages = Math.ceil(pamiQuestions.length / questionsPerPage);
  const totalPagesDisplay = totalPages + 1;
  const isFinalQuestionsPage = currentPage === totalPages;
  const startIndex = currentPage * questionsPerPage;
  const visibleQuestions = pamiQuestions.slice(
    startIndex,
    startIndex + questionsPerPage,
  );
  const questionsFinalesComplete =
    questionsFinales.langueMaternelle !== null &&
    questionsFinales.bonnesConditions !== null &&
    questionsFinales.reponsesSinceres !== null;

  const reponses = evaluation.donnees.reponses as PamiReponses;
  const baseDefaultValues = pamiQuestions.reduce(
    (acc, q) => {
      acc[q.name] = reponses[q.name]?.orderedItems || q.statements;
      return acc;
    },
    {} as Record<string, PamiStatement[]>,
  );

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: baseDefaultValues as FormData,
  });

  useEffect(() => {
    reset();
    setStartTime(Date.now());
    return () => {
      reset();
    };
  }, [reset, setStartTime]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (form.formState.isDirty && !form.formState.isSubmitting) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [form.formState.isDirty, form.formState.isSubmitting]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const submitEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const updateResponses = useMutation(api.evaluations.mettreAJourReponses);

  const buildDonnees = (data: FormData, withQuestionsFinales = false) => ({
    type: TypeEvalEnum.FORM as const,
    template: TemplateFormEnum.PAMI,
    reponses: Object.entries(data).reduce(
      (acc, [questionName, statements]) => {
        acc[questionName] = {
          name: questionName,
          label: "",
          orderedItems: statements.map(
            ({ id, label, prefixLabel }: PamiStatement) => ({
              id,
              label,
              prefixLabel,
            }),
          ),
        };
        return acc;
      },
      {} as Record<
        string,
        { name: string; label: string; orderedItems: PamiStatement[] }
      >,
    ),
    ...(withQuestionsFinales && {
      questionsFinales: {
        langueMaternelle: questionsFinales.langueMaternelle as boolean,
        bonnesConditions: questionsFinales.bonnesConditions as boolean,
        reponsesSinceres: questionsFinales.reponsesSinceres as boolean,
      },
    }),
  });

  const onSubmit = async (data: FormData) => {
    try {
      const result = await submitEvaluation({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data, true),
      });

      if (isBackendError(result)) {
        console.error("Erreur:", result.erreur);
      } else {
        onComplete?.();
      }
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    }
  };

  const saveResponses = async (data: FormData) => {
    try {
      await updateResponses({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data),
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };

  function handleDragEnd(
    event: any,
    questionName: string,
    items: PamiStatement[],
  ) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newOrder = arrayMove(items, oldIndex, newIndex);

      // Mise à jour du formulaire
      form.setValue(questionName, newOrder);
    }
  }

  const goToPage = async (page: number, data: FormData) => {
    if (page >= 0 && page <= totalPages) {
      if (editable && !isFinalQuestionsPage) await saveResponses(data);
      setCurrentPage(page);
    }
  };

  return (
    <div className="h-[calc(100vh-61.48px)] border-box flex flex-col p-4 overflow-y-auto">
      <Form {...form}>
        <form
          className="flex flex-col gap-8 w-full max-w-3xl mx-auto py-8"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>
                {isFinalQuestionsPage
                  ? "Questions finales"
                  : `Question ${currentPage + 1} / ${totalPagesDisplay}`}
              </span>
            </div>
            <Progress
              value={((currentPage + 1) / totalPagesDisplay) * 100}
              className="h-1.5"
            />
          </div>

          {isFinalQuestionsPage ? (
            <div className="space-y-8">
              <p className="text-xl font-medium leading-relaxed">
                Avant de valider, merci de répondre aux questions suivantes :
              </p>
              {[
                {
                  key: "langueMaternelle" as const,
                  label: "J'ai passé le test dans ma langue maternelle",
                },
                {
                  key: "bonnesConditions" as const,
                  label:
                    "J'ai passé ce questionnaire dans de bonnes conditions",
                },
                {
                  key: "reponsesSinceres" as const,
                  label: "J'ai répondu de manière sincère et honnête",
                },
              ].map(({ key, label }) => (
                <div key={key} className="space-y-3">
                  <p className="text-base font-medium">{label}</p>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={
                        questionsFinales[key] === true ? "default" : "outline"
                      }
                      onClick={() =>
                        setQuestionsFinales((prev) => ({
                          ...prev,
                          [key]: true,
                        }))
                      }
                      disabled={!editable}
                    >
                      Oui
                    </Button>
                    <Button
                      type="button"
                      variant={
                        questionsFinales[key] === false ? "default" : "outline"
                      }
                      onClick={() =>
                        setQuestionsFinales((prev) => ({
                          ...prev,
                          [key]: false,
                        }))
                      }
                      disabled={!editable}
                    >
                      Non
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-6">
              <p className="text-xl font-medium leading-relaxed">
                {pamiInstructions}
              </p>
              {visibleQuestions.map((question) => (
                <FormField
                  key={question.name}
                  control={form.control}
                  name={question.name}
                  render={() => (
                    <FormItem>
                      <FormControl>
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event: any) =>
                            editable &&
                            handleDragEnd(
                              event,
                              question.name,
                              form.getValues(question.name),
                            )
                          }
                        >
                          <SortableContext
                            items={form.getValues(question.name) || []}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="mt-1 space-y-1">
                              {(form.getValues(question.name) || []).map(
                                (item: PamiStatement, index: number) => (
                                  <SortableItem
                                    key={item.id}
                                    id={item.id}
                                    label={item.label}
                                    index={index}
                                    editable={editable}
                                  />
                                ),
                              )}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
          )}

          {/* Navigation entre les pages */}
          <div className="flex justify-between gap-4 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => goToPage(currentPage - 1, form.getValues())}
              disabled={currentPage === 0}
            >
              ← Précédent
            </Button>

            {isFinalQuestionsPage ? (
              <Button
                type="submit"
                size="lg"
                className="font-bold bg-primary text-primary-foreground hover:bg-primary/90"
                disabled={
                  !editable ||
                  form.formState.isSubmitting ||
                  !questionsFinalesComplete
                }
              >
                {form.formState.isSubmitting
                  ? "Envoi en cours..."
                  : "✓ Valider mes réponses"}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  goToPage(currentPage + 1, form.getValues());
                }}
              >
                Suivant →
              </Button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
